import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  GraduationCap,
  Search,
  Paperclip,
  Settings2,
} from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { useCourseOptions } from "@/features/universityAndCourse/api/useCourses";
import { getCourseCountries } from "@/features/universityAndCourse/hooks/useCourseFilters";
import useCourseFilters from "@/features/universityAndCourse/hooks/useCourseFilters";
import FilterSection from "@/module/education/nicheSideRoutes/Course/components/FilterSection";
import AddCourseButton from "@/features/universityAndCourse/components/add-edit-Course/AddCourseButton";
import Toggle from "@/components/common/inputs/Toggle";
import { useAttachVariant } from "@/features/leads/api/usePaymentMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { refreshAfterDealCreate } from "@/module/utils/refreshAfterDealCreate";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { Dialog, DialogFooter } from "@/components/ui/library/dialog";
import PrefrenceFormRenderer from "@/components/ui/others/PrefrenceRenderers/PrefrenceFormRenderer";
import { CustomInputScopeSchema } from "@/types/general";
import { child_spesific_key } from "@/config/endpoints";

interface AttachCourseModalProps {
  open: boolean;
  onClose: () => void;
  /** The deal to attach to — omitted in create mode (see `createForLead`). */
  dealId?: number | null;
  dealName: string;
  /**
   * Deals only on attach: the lead has no deal yet, so confirming CREATES its
   * first deal carrying the picked course and preferences (`POST
   * /core/childs`, started at the lead's own stage) instead of patching an
   * existing deal.
   */
  createForLead?: { id: number; name?: string | null } | null;
}

const fmt = (n: number) => n.toLocaleString("en-IN");

const AttachCourseFormSchema = z.object({
  customInputScopes: z.array(CustomInputScopeSchema).optional(),
});

type AttachCourseFormData = z.infer<typeof AttachCourseFormSchema>;

export default function AttachCourseModal({
  open,
  onClose,
  dealId,
  dealName,
  createForLead,
}: AttachCourseModalProps) {
  // Only walk the full course catalog when the modal is actually open — the
  // dialog stays mounted (hidden) on the lead-detail view, so without this the
  // 50-page fetch ran on every lead open. On open it fetches the full list as
  // before (cached 5min, so reopen is instant); search behaviour is unchanged.
  const { courses, isLoading } = useCourseOptions({ enabled: open });
  const {
    search,
    setSearch,
    eligibleOnly,
    setEligibleOnly,
    filters,
    setFilter,
    resetFilters,
    filterConfigs,
    filteredCourses,
  } = useCourseFilters(courses);

  // Local form mirrors the application-creation contract: `customInputScopes`
  // is the array `PrefrenceFormRenderer` reads/writes via `getValues`/`setValue`.
  // The renderer also uses `control`/`errors` for the Select inputs.
  const formMethods = useForm<AttachCourseFormData>({
    resolver: zodResolver(AttachCourseFormSchema),
    mode: "onChange",
    defaultValues: { customInputScopes: [] },
  });
  const {
    control,
    getValues,
    setValue,
    reset: resetForm,
    formState: { errors },
  } = formMethods;
  const useFormHook = () => ({ errors, control, getValues, setValue });

  const [expandedUniId, setExpandedUniId] = useState<number | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  // The deal's Country preference on the right is auto-filled and LOCKED:
  //   1. to the Country picked in the left-hand filter when one is picked —
  //      the operator has already said which country this deal is for, and
  //      it is the only thing that can disambiguate a university with
  //      campuses in several countries;
  //   2. otherwise to the picked course's country (via its university), the
  //      same value the left filter's options are built from.
  // The filter value is only honoured while the picked course actually sits
  // in that country (a course picked before the filter was changed must not
  // be stamped with a country it is not in).
  const selectedCourseCountries = useMemo(() => {
    const fromCourse = getCourseCountries(selectedCourse);
    const picked = String(filters.country ?? "").trim();
    if (!picked || picked.toLowerCase() === "all") return fromCourse;
    const pickedKey = picked.toLowerCase();
    const courseInPicked =
      fromCourse.length === 0 ||
      fromCourse.some((c) => c.trim().toLowerCase() === pickedKey);
    return courseInPicked ? [picked] : fromCourse;
  }, [selectedCourse, filters.country]);

  // `useWatch` is more reliable than `watch()` for array-typed fields
  // that get mutated through nested Controllers — `watch()` in the
  // parent didn't always re-fire on `field.onChange` calls inside the
  // children, so the gate was stuck on the first snapshot. Default to
  // `[]` so the dep array is stable when the form first mounts.
  const watchedScopes =
    useWatch({ control, name: "customInputScopes" }) ?? [];

  // Validation rule (matches what the user sees on screen):
  //   - A course must be picked.
  //   - Every rendered preference field must hold a value. We read this
  //     from the form's own scope array because `PrefrenceFormRenderer`
  //     writes one entry per rendered field via `buildAlignedScopes`
  //     (including the auto-filled locked Country row). If no entries
  //     exist yet (renderer hasn't mounted because no course is picked),
  //     we treat it as incomplete; the course gate also blocks in that
  //     case, so the message reads naturally.
  const preferenceStats = useMemo(() => {
    let total = 0;
    let filled = 0;
    for (const s of watchedScopes) {
      if (!s) continue;
      total += 1;
      const hasOption =
        Array.isArray(s.optionIds) && s.optionIds.length > 0;
      const hasText =
        typeof s.textValue === "string" && s.textValue.trim().length > 0;
      if (hasOption || hasText) filled += 1;
    }
    return { total, filled };
  }, [watchedScopes]);

  const preferencesComplete =
    Boolean(selectedCourse) &&
    preferenceStats.total > 0 &&
    preferenceStats.filled === preferenceStats.total;

  // Group filtered courses by university for the expandable list view that the
  // attach modal already used.
  const grouped = useMemo(() => {
    if (!filteredCourses?.length) return [];
    const map = new Map<number, { university: any; courses: any[] }>();
    for (const c of filteredCourses) {
      const uniId = c.universityId;
      if (!map.has(uniId)) {
        map.set(uniId, { university: c.university, courses: [] });
      }
      map.get(uniId)!.courses.push(c);
    }
    return Array.from(map.values());
  }, [filteredCourses]);

  const attachVariant = useAttachVariant();
  const { createChild } = useChildMutations();
  const isCreate = !!createForLead?.id;
  const isPending = attachVariant.isPending || createChild.isPending;

  // Module scopes drive default-resolution for the picked course (same data
  // the EducationProvider feeds to PrefrenceFormRenderer).
  const moduleScopes = useMemo(
    () =>
      selectedCourse?.id
        ? [
            {
              nicheModelName: "course" as const,
              nicheItemId: selectedCourse.id,
            },
          ]
        : [],
    [selectedCourse?.id]
  );

  // Drop any custom-field selections when the picked course changes so the
  // renderer's auto-fill rebuilds from scratch for the new course context.
  useEffect(() => {
    resetForm({ customInputScopes: [] });
  }, [selectedCourse?.id, resetForm]);

  const resetState = () => {
    setExpandedUniId(null);
    setSelectedCourse(null);
    resetFilters();
    resetForm({ customInputScopes: [] });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Mutation goes through PATCH /core/child/:id which is wrapped by the
  // ChildUpdate transition on the server — that scenario already emits
  // activityEmitter.dataUpdate with metadata.moduleScopes, so the attach
  // action is recorded in the activity log automatically. The transition
  // also runs customScopeRepo.customModelSync, so passing customInputScopes
  // mirrors the create-application flow and persists per-course defaults.
  const handleConfirm = () => {
    if (!selectedCourse) return;
    const totalDealValue = Number(selectedCourse.minFee) || 0;
    const customInputScopes = getValues("customInputScopes") ?? [];
    const moduleScopes = [
      { nicheModelName: "course", nicheItemId: selectedCourse.id },
    ];
    const financials = {
      totalDealValue,
      netAmount: totalDealValue,
      discountAmount: 0,
    };
    const done = () => {
      resetState();
      onClose();
    };

    if (isCreate) {
      // First deal, born with the course attached — the same payload the Add
      // Application flow sends to `child-creation`. `startAtLeadStage` puts
      // it where the auto-created deal would have sat (the lead's Choices
      // stage).
      const leadId = Number(createForLead!.id);
      // UNIVERSITY FIRST — "<University> - <Course>" (the server mints the same
      // when the name is missing; sent explicitly so the request is
      // self-describing).
      const name = [selectedCourse.university?.name, selectedCourse.name]
        .map((s: unknown) => (typeof s === "string" ? s.trim() : ""))
        .filter(Boolean)
        .join(" - ");
      createChild.mutate(
        {
          leadId,
          ...(name ? { name } : {}),
          moduleScopes,
          financials,
          customInputScopes,
          startAtLeadStage: true,
        },
        {
          onSuccess: () => {
            refreshAfterDealCreate(leadId);
            done();
          },
        },
      );
      return;
    }

    attachVariant.mutate(
      { childId: Number(dealId), moduleScopes, financials, customInputScopes },
      { onSuccess: done }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="w-[min(96vw,1040px)] sm:max-w-260 rounded-[24px] p-0 gap-0 max-h-[min(88vh,720px)] flex flex-col overflow-hidden">
        <DialogHeader className="px-4 pt-[11px] pb-[11px] border-b border-[#e6e8e7]">
          <div className="flex items-center justify-between gap-3 pr-8">
            <div className="min-w-0">
              <p className="text-[18px] font-medium text-black tracking-[-0.72px] leading-normal">
                Attach Course
              </p>
              <DialogDescription className="text-[#505e59] text-[13px] tracking-[-0.52px] leading-[1.35] mt-0.5">
                {isCreate
                  ? `${dealName} · creates this lead's first deal`
                  : dealName}
              </DialogDescription>
            </div>
            {/* Course missing from the list? Add it without leaving the */}
            {/* modal — RBAC-gated on the Universities & Courses module. */}
            <AddCourseButton
              label="Add Course"
              className="shrink-0 flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--color-green-4)] bg-gradient-to-b from-[#dcf5e7] to-[#c2f5da] px-3 text-[12px] font-medium tracking-[-0.2px] text-[var(--color-black-9)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_2px_rgba(13,38,28,0.08)] transition-transform hover:-translate-y-[1px]"
            />
          </div>
        </DialogHeader>

        {/* Side-by-side body: course picker on the left, preferences on
            the right with a visible divider. Both columns scroll
            independently so the outer modal stays a tidy fixed height. */}
        <div className="flex-1 min-h-0 flex flex-row overflow-hidden">
        <div className="flex-1 min-w-0 flex flex-col border-r border-[#e6e8e7]">
        <div className="px-4 pt-3 pb-2 flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#9ba3a0]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by university or course name"
              className="w-full h-9 pl-8 pr-3 text-[13px] rounded-lg border border-[#e6e8e7] bg-[#f7f9f8] text-[#1e302a] placeholder:text-[#b3b8b5] focus:outline-none focus:border-[var(--color-success)] focus:ring-1 focus:ring-[var(--color-success)]/20 transition-colors"
            />
          </div>

          <FilterSection
            configs={filterConfigs}
            filters={filters}
            onFilterChange={setFilter}
            className="sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3"
          />

          <div className="flex items-center justify-between pt-1">
            <span className="text-[12px] text-[#697571]">
              Courses ({filteredCourses.length})
            </span>
            <div className="flex items-center gap-2">
              <p
                className="text-[12px] font-medium"
                style={{
                  background:
                    "linear-gradient(166.807deg, rgb(13, 38, 28) 59.642%, rgb(49, 155, 114) 107.05%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Eligible courses only
              </p>
              <Toggle checked={eligibleOnly} onChange={setEligibleOnly} />
            </div>
          </div>
        </div>

        <div className="px-4 pb-3 flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-5 animate-spin text-[#9ba3a0]" />
            </div>
          ) : grouped.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <GraduationCap className="size-8 text-[#c5c9c7] mb-2" />
              <p className="text-[13px] text-[#8a918d]">
                No courses match the current filters
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {grouped.map((group) => {
                const uni = group.university;
                const uniName: string = uni?.name ?? "";
                const isExpanded = expandedUniId === uni?.id;

                return (
                  <div
                    key={uni?.id ?? Math.random()}
                    className="rounded-xl border border-[#e6e8e7] overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedUniId(isExpanded ? null : uni?.id ?? null)
                      }
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[#f7f9f8] transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="size-3.5 text-[#9ba3a0] shrink-0" />
                      ) : (
                        <ChevronRight className="size-3.5 text-[#9ba3a0] shrink-0" />
                      )}
                      <span className="text-[13px] font-semibold text-[#1e302a] truncate flex-1">
                        {uni?.name ?? "Unnamed University"}
                      </span>
                      <span className="text-[11px] text-[#9ba3a0] shrink-0">
                        {group.courses.length} course
                        {group.courses.length !== 1 ? "s" : ""}
                      </span>
                    </button>

                    {isExpanded && group.courses.length > 0 && (
                      <div className="border-t border-[#f0f1f0] bg-[#fafbfa]">
                        {group.courses.map((c: any) => {
                          const fee = Number(c.minFee) || 0;
                          const isSelected = selectedCourse?.id === c.id;

                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() =>
                                setSelectedCourse(isSelected ? null : c)
                              }
                              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
                                isSelected
                                  ? "bg-[#f0faf5] border-l-2 border-l-[var(--color-success)]"
                                  : "hover:bg-[#f0f1f0] border-l-2 border-l-transparent"
                              }`}
                            >
                              <div
                                className={`size-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                  isSelected
                                    ? "border-[var(--color-success)] bg-[var(--color-success)]"
                                    : "border-[#c5c9c7]"
                                }`}
                              >
                                {isSelected && (
                                  <Check
                                    className="size-2.5 text-white"
                                    strokeWidth={3}
                                  />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span
                                  className={`text-[13px] truncate block ${
                                    isSelected
                                      ? "font-semibold text-[var(--color-success)]"
                                      : "text-[#374741]"
                                  }`}
                                >
                                  {c.name}
                                </span>
                                {/* University rides on every row (not just the
                                    group header) so a course is still
                                    identified once the header scrolls away or
                                    the row is read on its own. */}
                                {(uniName || c.courseType) && (
                                  <span className="text-[11px] text-[#9ba3a0] truncate block">
                                    {[uniName, c.courseType]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                )}
                              </div>
                              {fee > 0 && (
                                <span
                                  className={`text-[13px] tabular-nums shrink-0 ${
                                    isSelected
                                      ? "font-bold text-[var(--color-success)]"
                                      : "font-medium text-[#697571]"
                                  }`}
                                >
                                  ₹{fmt(fee)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        </div>
        {/* RIGHT: preferences panel — always rendered for visual stability;
            shows a placeholder until a course is picked. Required because
            most downstream automations (intake routing, country gating,
            degree-type reports) depend on these values being set. */}
        <div className="w-85 shrink-0 flex flex-col bg-[#fafbfa]">
          <div className="px-4 pt-3 pb-2.5 border-b border-[#e6e8e7] flex items-center gap-2">
            <Settings2 className="size-3.5 text-(--color-success) shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[#1e302a] leading-tight">
                Preferences{" "}
                <span className="text-(--color-error)">*</span>
              </p>
              <p className="text-[11px] text-[#697571] leading-tight mt-0.5">
                All fields are required
              </p>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
            {selectedCourse ? (
              <PrefrenceFormRenderer
                hook={useFormHook}
                modelName="child"
                queryKey={child_spesific_key}
                moduleScopes={moduleScopes}
                countryValues={selectedCourseCountries}
                col={1}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center px-2">
                <Settings2 className="size-7 text-[#c5c9c7] mb-2" />
                <p className="text-[12px] text-[#8a918d] leading-snug">
                  Pick a course on the left to set its preferences
                </p>
              </div>
            )}
          </div>
        </div>
        </div>

        <DialogFooter className="px-4 pb-4 pt-2 border-t border-[#e6e8e7] flex sm:justify-between items-center gap-2">
          <div className="text-[12px] text-[#697571] flex-1 min-w-0 truncate">
            {!selectedCourse ? (
              <span className="text-[#9ba3a0]">Select a course to attach</span>
            ) : !preferencesComplete ? (
              <span className="text-(--color-error)">
                Fill all preference fields to continue
                {preferenceStats.total > 0 && (
                  <>
                    {" "}
                    ({preferenceStats.filled}/{preferenceStats.total})
                  </>
                )}
              </span>
            ) : (
              <span>
                Selected:{" "}
                <strong className="text-[#1e302a]">
                  {selectedCourse.name}
                </strong>
                {Number(selectedCourse.minFee) > 0 && (
                  <>
                    {" · "}₹{fmt(Number(selectedCourse.minFee))}
                  </>
                )}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <DialogClose asChild>
              <Button variant="cancel" disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>

            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={
                !selectedCourse ||
                !preferencesComplete ||
                isPending
              }
            >
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Paperclip className="size-4" />
              )}
              {isPending
                ? "Attaching…"
                : isCreate
                  ? "Attach & create deal"
                  : "Attach"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
